/**
 * MRI Velocity Unwrapping - Iterative Poisson Solver with Data Fidelity (Gauss-Seidel SOR)
 *
 * ============================================================================
 * MATHEMATICAL FRAMEWORK
 * ============================================================================
 *
 * Solves the regularized Poisson equation:
 *   ∇²φ - λ(φ - φ₀) = ∇·ψ
 *
 * Where:
 *   φ   = unwrapped velocity field (solution we seek)
 *   φ₀  = original wrapped velocity field (reference/anchor)
 *   ψ   = unwrapped gradient field (ψₓ, ψᵧ)
 *   λ   = fidelity weight (0=pure Poisson, 1=preserve original)
 *   ∇²  = Laplacian operator (∂²/∂x² + ∂²/∂y²)
 *   ∇·  = divergence operator (∂/∂x + ∂/∂y)
 *
 * ============================================================================
 * ALGORITHM PIPELINE
 * ============================================================================
 *
 * 1. TEMPORAL UNWRAPPING (optional, if numFrames > 1):
 *    - Linear mode: Forward/backward passes
 *    - Cyclic mode: Forward/backward + cycle closure
 *
 * 2. SPATIAL UNWRAPPING (per frame):
 *    a) Early exit check: maxJump ≤ threshold? → skip
 *    b) Compute wrapped gradients: ψ_wrapped = ∇φ₀
 *    c) Unwrap gradients: if |ψ| > threshold → ψ ± 2venc
 *    d) Compute divergence: rhs = ∇·ψ_unwrapped
 *    e) Solve Poisson: ∇²φ - λ(φ - φ₀) = rhs
 *    f) Apply ROI mask
 *
 * ============================================================================
 * PUBLIC INTERFACE
 * ============================================================================
 *
 *  - constructor({width, height, venc, roi?})
 *  - unwrapCine(velocityFrames, magnitudeFrames): Float32Array[]
 *  - unwrap(velocityData, magnitudeData): Float32Array
 *  - unwrapFrames(velocityFrames, magnitudeFrames): Float32Array[]
 *  - setTemporalMode(mode: 'linear' | 'cyclic')
 *  - setROI(roi: ROI) / clearROI()
 *
 * ============================================================================
 * ROI BEHAVIOR
 * ============================================================================
 *
 *  - If ROI provided: solves only inside ROI with Neumann boundary conditions
 *    • Neumann BC: ∂φ/∂n = 0 at boundaries (zero normal derivative)
 *    • Physically: "no flow" across ROI boundary
 *  - If no ROI: processes full image
 *
 * ============================================================================
 * ADVANTAGES OVER DCT
 * ============================================================================
 *
 *  ✓ Works efficiently with masked/irregular regions (ROI support)
 *  ✓ Handles ROI boundaries naturally (Neumann BC)
 *  ✓ More memory efficient for large images
 *  ✓ Data fidelity term prevents over-smoothing
 */

export interface ROI {
  bbox: { xMin: number; yMin: number; xMax: number; yMax: number };
  polygon: Array<{ x: number; y: number }>;
}

export class VelocityAntiAliaser {
  // ============================================================================
  // PUBLIC PROPERTIES
  // ============================================================================

  /**
   * Image dimensions (pixels)
   */
  width: number;
  height: number;

  /**
   * Velocity encoding (cm/s)
   *
   * Physical meaning:
   *   - MRI phase encoding maps velocity to phase: φ = (π·v) / venc
   *   - Velocity range: [-venc, +venc] maps to phase [-π, +π]
   *   - Aliasing occurs when |v| > venc (phase wraps beyond ±π)
   *   - Wrap amount: 2venc (full cycle from +venc to -venc)
   */
  venc: number;

  // ============================================================================
  // PRIVATE CONFIGURATION
  // ============================================================================

  private _roi: ROI | null = null;

  /**
   * Enable temporal unwrapping across cardiac phases
   *
   * Recommended: true for cardiac cine MRI
   * Disable only for single-frame or non-sequential data
   */
  private _doTemporal = true;

  /**
   * Enable diagnostic logging to console
   *
   * Logs:
   *   - Frame-by-frame max discontinuity
   *   - Convergence progress (every 100 iterations)
   *   - Correction counts (temporal, spatial)
   *   - Timing information
   */
  private _logDiagnostics = true;

  /**
   * Temporal unwrapping mode
   *
   * ========================================================================
   * MODE: 'linear' (default, conservative)
   * ========================================================================
   *
   * Algorithm:
   *   - Forward pass (t=0→N-1): φ[t] corrected based on φ[t-1]
   *   - Backward pass (t=N-1→0): φ[t] corrected based on φ[t+1]
   *
   * Boundary condition: None (φ[0] and φ[N-1] are independent)
   *
   * Use when:
   *   ✓ Uncertain if data is truly periodic
   *   ✓ Non-periodic temporal sequence
   *   ✓ Conservative approach desired
   *   ✓ Debugging/validation
   *
   * Limitation:
   *   ✗ May leave discontinuity at cycle boundary (φ[N-1] → φ[0])
   *
   * ========================================================================
   * MODE: 'cyclic' (for periodic cardiac cine)
   * ========================================================================
   *
   * Algorithm:
   *   - Forward pass (t=0→N-1): same as linear
   *   - Backward pass (t=N-1→0): same as linear
   *   - Closure pass: enforce φ[0] ≈ φ[N-1]
   *
   * Boundary condition: Cyclic (φ[0] connects to φ[N-1])
   *
   * Mathematical constraint:
   *   Minimize: |φ[0] - φ[N-1]|
   *   Subject to: φ[t] = φ_wrapped[t] + k[t]·2venc, k ∈ ℤ
   *
   * Use when:
   *   ✓ True cardiac cine sequence (periodic)
   *   ✓ End-diastole to end-diastole acquisition
   *   ✓ Discontinuity observed at cycle boundary with linear mode
   *
   * Limitation:
   *   ✗ May over-correct if data not truly periodic
   *   ✗ Assumes cardiac cycle properly captured
   *
   * ========================================================================
   * SELECTION GUIDE
   * ========================================================================
   *
   * Start with 'linear':
   *   1. Run unwrapping
   *   2. Check φ[0] vs φ[N-1] discontinuity
   *   3. If large discontinuity and data is periodic → switch to 'cyclic'
   *
   * Typical discontinuity check:
   *   Δ_boundary = mean|φ[0,i] - φ[N-1,i]| over ROI
   *   If Δ_boundary > 0.5·venc → consider 'cyclic' mode
   */
  private _temporalMode: 'linear' | 'cyclic' = 'linear';

  // ============================================================================
  // ITERATIVE SOLVER PARAMETERS
  // ============================================================================

  /**
   * Maximum iterations for Gauss-Seidel solver
   *
   * Convergence behavior:
   *   - Typical: 50-200 iterations for 256×256 images
   *   - Larger images: 200-500 iterations
   *   - Complex wrapping: may need 500+ iterations
   *
   * If not converging:
   *   - Increase _maxIterations
   *   - Decrease _tolerance (looser convergence)
   *   - Adjust _omega (try values 1.5-1.9)
   *   - Check for severe aliasing (multiple wraps)
   *
   * Trade-off:
   *   - Higher → more accurate, slower
   *   - Lower → faster, may not converge
   */
  private _maxIterations = 500;

  /**
   * Convergence tolerance (maximum pixel change between iterations)
   *
   * Stopping criterion:
   *   Algorithm stops when: max_i |φ^(k+1)[i] - φ^k[i]| < tolerance
   *
   * Physical meaning:
   *   - Maximum velocity change (cm/s) between iterations
   *   - 1e-4: velocity stable to 0.0001 cm/s
   *
   * Typical values:
   *   - 1e-3: Loose (faster, less accurate)
   *   - 1e-4: Balanced (recommended)
   *   - 1e-5: Tight (slower, more accurate)
   *
   * For venc=150 cm/s:
   *   - 1e-4 → 0.00007% relative error
   */
  private _tolerance = 1e-4;

  /**
   * SOR (Successive Over-Relaxation) parameter ω
   *
   * ========================================================================
   * MATHEMATICAL FORMULATION
   * ========================================================================
   *
   * Standard Gauss-Seidel update:
   *   φ^(k+1)[i] = φ_GS[i]  (new value replaces old)
   *
   * SOR update:
   *   φ^(k+1)[i] = φ^k[i] + ω·(φ_GS[i] - φ^k[i])
   *              = (1-ω)·φ^k[i] + ω·φ_GS[i]
   *
   * Where φ_GS[i] is the Gauss-Seidel update value
   *
   * ========================================================================
   * PARAMETER VALUES
   * ========================================================================
   *
   * ω = 1.0: Standard Gauss-Seidel
   *   - No relaxation
   *   - Stable but slow convergence
   *
   * ω ∈ (1, 2): Over-relaxation (recommended)
   *   - Accelerates convergence
   *   - Must satisfy: 0 < ω < 2 for stability
   *
   * ω ∈ [1.8, 1.95]: Optimal range for 2D Poisson
   *   - Theoretical optimal ≈ 1.93 for square grids
   *   - Practical: 1.8 (safe), 1.9 (aggressive)
   *
   * ω ≥ 2: Unstable
   *   - Diverges (do not use)
   *
   * ========================================================================
   * CONVERGENCE SPEED
   * ========================================================================
   *
   * Typical iterations to converge (256×256 grid):
   *   ω = 1.0:  ~500 iterations
   *   ω = 1.5:  ~200 iterations
   *   ω = 1.8:  ~100 iterations
   *   ω = 1.9:  ~80 iterations
   *
   * ========================================================================
   * TUNING GUIDE
   * ========================================================================
   *
   * Default (1.8): Safe, good performance
   *
   * If oscillating/not converging:
   *   → Decrease to 1.5-1.7 (more stable)
   *
   * If converging slowly:
   *   → Increase to 1.85-1.92 (faster, less stable)
   *
   * Never exceed 1.95 in practice
   */
  private _omega = 1.8;

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(opts: {
    width: number;
    height: number;
    venc: number;
    roi?: ROI | null;
  }) {
    const { width, height, venc } = opts;
    if (
      !Number.isFinite(width)
      || !Number.isFinite(height)
      || width <= 0
      || height <= 0
    ) {
      throw new Error('width and height must be positive numbers');
    }
    if (!Number.isFinite(venc) || venc <= 0) {
      throw new Error('venc must be positive');
    }
    this.width = width | 0;
    this.height = height | 0;
    this.venc = venc;

    if (opts.roi) this.setROI(opts.roi);
  }

  setROI(roi: ROI) {
    VelocityAntiAliaser._validateROI(roi);
    this._roi = roi;
  }

  clearROI() {
    this._roi = null;
  }

  /**
   * Set temporal unwrapping mode
   *
   * @param mode - 'linear' (safe default) or 'cyclic' (for true cardiac cycles)
   *
   * Can be changed between calls to unwrapCine() to compare results
   */
  setTemporalMode(mode: 'linear' | 'cyclic') {
    this._temporalMode = mode;
  }

  // ============================================================================
  // PUBLIC API: Main unwrapping entry point
  // ============================================================================

  unwrapCine(
    velocityFrames: Array<Float32Array | number[]>,
    magnitudeFrames: Array<Float32Array | number[]>,
  ): Float32Array[] {
    if (velocityFrames.length !== magnitudeFrames.length)
      throw new Error('Frame count mismatch');
    if (velocityFrames.length === 0) return [];

    const numFrames = velocityFrames.length;
    const n = this.width * this.height;

    // Copy inputs to prevent modification of original data
    const velFrames = velocityFrames.map(f =>
      VelocityAntiAliaser._toFloat32Copy(f),
    );
    // const _magFrames = magnitudeFrames.map(f => this._toFloat32Copy(f));

    // Build active processing mask
    // mask[i] = 1: process this pixel
    // mask[i] = 0: skip this pixel (outside ROI)
    const activeMask = this._roi
      ? this._buildROIMask(n, this._roi)
      : VelocityAntiAliaser._fullMask(n);

    console.log(
      `Unwrapping ${numFrames} frames (Iterative Poisson), venc=±${this.venc}${
        this._roi ? ', ROI=ON' : ', ROI=OFF'
      }, temporal=${this._temporalMode}`,
    );

    // -------------------------------------------------------------------------
    // STEP 1: Temporal unwrapping (if enabled and multiple frames)
    // -------------------------------------------------------------------------
    // Corrects frame-to-frame discontinuities before spatial unwrapping
    // Assumption: velocity should vary smoothly between cardiac phases

    if (this._doTemporal && numFrames > 1) {
      if (this._temporalMode === 'cyclic') {
        const { fwd, bwd, cyclic } = this._unwrapTemporalCyclic(
          velFrames,
          activeMask,
        );
        console.log(
          `Temporal (cyclic): ${fwd} fwd, ${bwd} bwd, ${cyclic} cyclic corrections`,
        );
      }
      else {
        const { fwd, bwd } = this._unwrapTemporalLinear(velFrames, activeMask);
        console.log(`Temporal (linear): ${fwd} fwd, ${bwd} bwd corrections`);
      }
    }

    // -------------------------------------------------------------------------
    // STEP 2: Spatial unwrapping per frame
    // -------------------------------------------------------------------------
    // Each frame processed independently using Poisson solver

    const outFrames = Array.from<Float32Array>({ length: numFrames });
    for (let t = 0; t < numFrames; t++) {
      const v = velFrames[t];

      // Diagnostic: check if unwrapping is needed for this frame
      if (this._logDiagnostics) {
        const maxJump = this._maxAbsNeighborJump(v, activeMask);
        console.log(
          `Frame ${t}: maxNeighborJump=${maxJump.toFixed(6)} (venc=${
            this.venc
          })`,
        );
      }

      const before = VelocityAntiAliaser._nowMs();
      const unwrapped = this._unwrapSpatialPoisson(v, activeMask);
      const after = VelocityAntiAliaser._nowMs();

      console.log(
        `Frame ${t}: Poisson unwrap, time=${(after - before).toFixed(1)}ms`,
      );

      outFrames[t] = unwrapped;
    }

    return outFrames;
  }

  unwrap(
    velocityData: Float32Array | number[],
    magnitudeData: Float32Array | number[],
  ): Float32Array {
    return this.unwrapCine([velocityData], [magnitudeData])[0];
  }

  unwrapFrames(
    velocityFrames: Array<Float32Array | number[]>,
    magnitudeFrames: Array<Float32Array | number[]>,
  ): Float32Array[] {
    return this.unwrapCine(velocityFrames, magnitudeFrames);
  }

  // ============================================================================
  // SPATIAL UNWRAPPING: Poisson-based phase reconstruction
  // ============================================================================
  /**
   * Unwraps a single velocity frame using Poisson reconstruction
   *
   * MATHEMATICAL PIPELINE:
   * ======================
   *
   * Input: φ₀ (wrapped velocity field)
   * Output: φ (unwrapped velocity field)
   *
   * Steps:
   * 1. Compute gradients: ψ_wrapped = ∇φ₀
   * 2. Unwrap gradients: ψ_unwrapped (remove 2π jumps)
   * 3. Compute divergence: rhs = ∇·ψ_unwrapped
   * 4. Solve: ∇²φ - λ(φ - φ₀) = rhs
   * 5. Apply ROI mask
   *
   * @param velocity - Wrapped velocity field φ₀ (cm/s)
   * @param mask - Processing mask (1=active, 0=skip)
   * @returns Unwrapped velocity field φ (cm/s)
   */
  private _unwrapSpatialPoisson(
    velocity: Float32Array,
    mask: Uint8Array,
  ): Float32Array {
    const w = this.width;
    const h = this.height;

    /**
     * Wrap range: full aliasing cycle
     *
     * Physics:
     *   - Velocity range: [-venc, +venc]
     *   - When v > +venc: wraps to v - 2venc (appears negative)
     *   - When v < -venc: wraps to v + 2venc (appears positive)
     *   - Correction amount: ±2venc
     */
    const wrapRange = 2 * this.venc;

    // -------------------------------------------------------------------------
    // EARLY EXIT: Check if unwrapping is needed
    // -------------------------------------------------------------------------
    // If max neighbor difference ≤ threshold, no aliasing present → skip

    const maxJump = this._maxAbsNeighborJump(velocity, mask);

    /**
     * Unwrap threshold with safety buffer
     *
     * Formula: threshold = venc × (1 + buffer)
     *
     * Current: 1.2 → 20% buffer
     *
     * RATIONALE:
     * ==========
     *
     * Exact venc threshold problematic due to:
     *   - Noise: creates false discontinuities near ±venc
     *   - Edge effects: sharp anatomical boundaries
     *   - Partial volume: mixing of velocities
     *
     * Buffer provides safety margin:
     *   - Jumps < threshold: likely noise/edges → keep wrapped
     *   - Jumps > threshold: likely aliasing → unwrap
     *
     * PARAMETER TUNING:
     * =================
     *
     * Buffer = 0.05-0.10 (5-10%): Aggressive
     *   ✓ Catches more wraps
     *   ✗ May unwrap noise/edges
     *   Use: Clean data, low noise
     *
     * Buffer = 0.15-0.20 (15-20%): Balanced (recommended)
     *   ✓ Good trade-off
     *   ✓ Handles moderate noise
     *   Use: Typical cardiac MRI
     *
     * Buffer = 0.25-0.30 (25-30%): Conservative
     *   ✓ Avoids false positives
     *   ✗ May miss true wraps
     *   Use: Very noisy data
     *
     * EXAMPLE (venc = 150 cm/s):
     * ==========================
     *
     * Buffer 0.20 → threshold = 180 cm/s
     *   - Gradient < 180: assumed smooth (keep)
     *   - Gradient > 180: assumed wrapped (unwrap)
     */
    const unwrapThreshold = this.venc * 1.2; // 20% buffer

    if (maxJump <= unwrapThreshold) {
      console.log(
        `No aliasing (maxJump=${maxJump.toFixed(
          3,
        )} <= ${unwrapThreshold.toFixed(3)}) - skipping`,
      );
      return VelocityAntiAliaser._toFloat32Copy(velocity);
    }

    // -------------------------------------------------------------------------
    // STEP 1: Compute wrapped gradients using forward differences
    // -------------------------------------------------------------------------
    /**
     * Discrete gradient operator (forward differences):
     *
     * ψₓ[i,j] = (φ[i+1,j] - φ[i,j]) / Δx    (X gradient)
     * ψᵧ[i,j] = (φ[i,j+1] - φ[i,j]) / Δy    (Y gradient)
     *
     * With unit grid spacing (Δx = Δy = 1 pixel):
     *
     * ψₓ[i,j] = φ[i+1,j] - φ[i,j]
     * ψᵧ[i,j] = φ[i,j+1] - φ[i,j]
     *
     * Boundary condition (Neumann): Zero gradient at edges
     *   ψ[boundary] = 0
     */

    const gradX = new Float32Array(w * h);
    const gradY = new Float32Array(w * h);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;

        // X gradient: ∂φ/∂x
        if (x < w - 1) {
          gradX[i] = velocity[i + 1] - velocity[i];
        }
        else {
          gradX[i] = 0; // Neumann BC: ∂φ/∂x = 0 at right edge
        }

        // Y gradient: ∂φ/∂y
        if (y < h - 1) {
          gradY[i] = velocity[i + w] - velocity[i];
        }
        else {
          gradY[i] = 0; // Neumann BC: ∂φ/∂y = 0 at bottom edge
        }
      }
    }

    // -------------------------------------------------------------------------
    // STEP 2: Unwrap gradients (remove 2π phase jumps)
    // -------------------------------------------------------------------------
    /**
     * Gradient unwrapping logic:
     *
     * THEORY:
     * =======
     *
     * True gradient: ψ_true = ∇φ_true
     * Wrapped gradient: ψ_wrapped = ∇(φ_true mod 2venc)
     *
     * At aliasing boundary:
     *   - φ_true crosses ±venc
     *   - φ_wrapped has discontinuity ≈ ±2venc
     *   - ψ_wrapped has large magnitude (> threshold)
     *
     * DETECTION:
     * ==========
     *
     * If ψ > +threshold:
     *   → φ increased too much (wrapped from +venc to -venc)
     *   → True gradient smaller: ψ_true = ψ_wrapped - 2venc
     *
     * If ψ < -threshold:
     *   → φ decreased too much (wrapped from -venc to +venc)
     *   → True gradient larger: ψ_true = ψ_wrapped + 2venc
     *
     * Otherwise:
     *   → No wrap: ψ_true = ψ_wrapped
     *
     * EXAMPLE (venc = 100, threshold = 120):
     * ======================================
     *
     * Case 1: Positive wrap
     *   φ[i] = +95, φ[i+1] = -95 (wrapped)
     *   ψ_wrapped = -95 - 95 = -190 (< -120)
     *   ψ_true = -190 + 200 = +10 (correct!)
     *
     * Case 2: Negative wrap
     *   φ[i] = -95, φ[i+1] = +95 (wrapped)
     *   ψ_wrapped = +95 - (-95) = +190 (> +120)
     *   ψ_true = +190 - 200 = -10 (correct!)
     *
     * Case 3: No wrap
     *   φ[i] = +50, φ[i+1] = +60
     *   ψ_wrapped = +10 (within [-120, +120])
     *   ψ_true = +10 (no correction)
     */

    for (let i = 0; i < w * h; i++) {
      // Unwrap X gradient
      if (gradX[i] > unwrapThreshold) {
        gradX[i] -= wrapRange; // Remove positive wrap
      }
      else if (gradX[i] < -unwrapThreshold) {
        gradX[i] += wrapRange; // Remove negative wrap
      }
      // else: no wrap, keep original

      // Unwrap Y gradient
      if (gradY[i] > unwrapThreshold) {
        gradY[i] -= wrapRange;
      }
      else if (gradY[i] < -unwrapThreshold) {
        gradY[i] += wrapRange;
      }
    }

    // -------------------------------------------------------------------------
    // STEP 3: Compute divergence of unwrapped gradients
    // -------------------------------------------------------------------------
    /**
     * Divergence operator (backward differences):
     *
     * ∇·ψ = ∂ψₓ/∂x + ∂ψᵧ/∂y
     *
     * Discrete form (backward differences):
     *
     * ∂ψₓ/∂x ≈ (ψₓ[i,j] - ψₓ[i-1,j]) / Δx
     * ∂ψᵧ/∂y ≈ (ψᵧ[i,j] - ψᵧ[i,j-1]) / Δy
     *
     * With unit spacing (Δx = Δy = 1):
     *
     * div[i,j] = (ψₓ[i,j] - ψₓ[i-1,j]) + (ψᵧ[i,j] - ψᵧ[i,j-1])
     *
     * Boundary condition:
     *   At edges: assume ψ[-1] = 0
     *   div[0,j] = ψₓ[0,j] + (ψᵧ[0,j] - ψᵧ[0,j-1])
     *
     * PHYSICAL MEANING:
     * =================
     *
     * Divergence measures "source/sink" strength:
     *   - div > 0: flow expanding (source)
     *   - div < 0: flow converging (sink)
     *   - div = 0: flow incompressible (conservation)
     *
     * For unwrapped gradients:
     *   - div ≈ 0 in smooth regions (incompressible flow)
     *   - div ≠ 0 at wrap locations (gradient inconsistency)
     *
     * Poisson equation uses divergence as "forcing term"
     *   to reconstruct consistent velocity field
     */

    const rhs = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;

        if (!mask[i]) continue; // Only compute inside active region

        let div = 0;

        // ∂ψₓ/∂x: X-component of divergence
        if (x > 0) {
          div += gradX[i] - gradX[i - 1];
        }
        else {
          div += gradX[i]; // Boundary: assume ψₓ[-1] = 0
        }

        // ∂ψᵧ/∂y: Y-component of divergence
        if (y > 0) {
          div += gradY[i] - gradY[i - w];
        }
        else {
          div += gradY[i]; // Boundary: assume ψᵧ[-1] = 0
        }

        rhs[i] = div; // Right-hand side of Poisson equation
      }
    }

    // -------------------------------------------------------------------------
    // STEP 4: Solve regularized Poisson equation
    // -------------------------------------------------------------------------
    /**
     * Equation: ∇²φ - λ(φ - φ₀) = ∇·ψ
     *
     * where:
     *   - ∇²φ: Laplacian of unwrapped field (smoothness term)
     *   - λ(φ - φ₀): Data fidelity term (stay close to original)
     *   - ∇·ψ: Divergence of unwrapped gradients (compatibility)
     *
     * Balance controlled by λ (set inside solver)
     */

    const unwrapped = this._solvePoissonGaussSeidel(rhs, mask, velocity);

    // -------------------------------------------------------------------------
    // STEP 5: Apply ROI mask - preserve original data outside ROI
    // -------------------------------------------------------------------------
    const result = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      result[i] = mask[i] ? unwrapped[i] : velocity[i];
    }

    return result;
  }

  // ============================================================================
  // ITERATIVE SOLVER: Gauss-Seidel with SOR and Data Fidelity
  // ============================================================================
  /**
   * Solves the regularized Poisson equation using iterative relaxation
   *
   * ========================================================================
   * EQUATION
   * ========================================================================
   *
   * ∇²φ - λ(φ - φ₀) = rhs
   *
   * where:
   *   φ   = unknown (unwrapped velocity)
   *   φ₀  = known (wrapped velocity, reference)
   *   λ   = fidelity weight
   *   rhs = ∇·ψ (divergence of unwrapped gradients)
   *
   * ========================================================================
   * DISCRETIZATION (5-point stencil, unit grid)
   * ========================================================================
   *
   * Laplacian: ∇²φ[i,j] ≈ (φ[left] + φ[right] + φ[up] + φ[down] - 4φ[center]) / h²
   *
   * With h=1:
   *   ∇²φ[i,j] = φ[left] + φ[right] + φ[up] + φ[down] - 4φ[i,j]
   *
   * Full equation:
   *   φ[left] + φ[right] + φ[up] + φ[down] - 4φ[i,j] - λ(φ[i,j] - φ₀[i,j]) = rhs[i,j]
   *
   * ========================================================================
   * GAUSS-SEIDEL UPDATE
   * ========================================================================
   *
   * Rearrange to solve for φ[i,j]:
   *
   * φ[i,j]·(4 + λ) = φ[left] + φ[right] + φ[up] + φ[down] + λ·φ₀[i,j] - rhs[i,j]
   *
   * φ_GS[i,j] = (sum_neighbors + λ·φ₀[i,j] - rhs[i,j]) / (count_neighbors + λ)
   *
   * For irregular domains (ROI):
   *   - count_neighbors ≤ 4 (only include active neighbors)
   *   - Natural Neumann BC at ROI boundary
   *
   * ========================================================================
   * SOR ACCELERATION
   * ========================================================================
   *
   * φ^(k+1) = φ^k + ω(φ_GS - φ^k)
   *
   * Accelerates convergence by "over-shooting" the Gauss-Seidel update
   *
   * ========================================================================
   * PARAMETERS
   * ========================================================================
   *
   * @param rhs - Right-hand side: ∇·ψ (divergence of unwrapped gradients)
   * @param mask - Processing mask (1=active, 0=skip)
   * @param originalData - Reference data φ₀ for fidelity term
   *
   * @returns unwrapped - Solution φ
   */
  private _solvePoissonGaussSeidel(
    rhs: Float32Array,
    mask: Uint8Array,
    originalData: Float32Array,
  ): Float32Array {
    const w = this.width;
    const h = this.height;
    // const n = w * h;

    // Initialize solution with original wrapped data (warm start)
    // This provides much better initial guess than zeros
    // Physically: start from wrapped data, make small corrections
    const phi = VelocityAntiAliaser._toFloat32Copy(originalData);

    /**
     * Data fidelity weight λ
     *
     * ====================================================================
     * PHYSICAL INTERPRETATION
     * ====================================================================
     *
     * Equation: ∇²φ - λ(φ - φ₀) = rhs
     *
     * Energy functional being minimized:
     *
     * E[φ] = ∫∫ [|∇φ - ψ|² + λ·|φ - φ₀|²] dA
     *        \_____________/   \__________/
     *        Gradient match    Data fidelity
     *
     * λ controls balance:
     *   - λ=0: Pure Poisson (∇²φ = rhs)
     *     • Ignores original data φ₀
     *     • Full reconstruction from gradients
     *     • May alter unwrapped regions
     *
     *   - λ→∞: Pure data fidelity (φ ≈ φ₀)
     *     • Ignores gradient constraints
     *     • No unwrapping (keeps wrapped data)
     *
     *   - λ~0.5: Balanced (recommended)
     *     • Corrects wraps via gradient term
     *     • Preserves unwrapped regions via fidelity term
     *
     * ====================================================================
     * EFFECT ON SOLUTION
     * ====================================================================
     *
     * Low λ (0.1-0.3): Aggressive unwrapping
     *   ✓ Strong gradient matching
     *   ✓ Smooths velocity field
     *   ✗ May change originally unwrapped regions
     *   ✗ Can introduce spurious corrections
     *   Use: Severe aliasing, trust gradients more than data
     *
     * Medium λ (0.5-1.0): Balanced (RECOMMENDED)
     *   ✓ Corrects wrapped regions
     *   ✓ Preserves unwrapped regions
     *   ✓ Good for most cardiac MRI
     *   Use: Standard application
     *
     * High λ (2.0-5.0): Conservative
     *   ✓ Minimal changes to original data
     *   ✓ Only corrects obvious wraps
     *   ✗ May miss subtle wrapping
     *   Use: Uncertain about wrapping, prefer original data
     *
     * ====================================================================
     * MATHEMATICAL ANALYSIS
     * ====================================================================
     *
     * Frequency domain interpretation:
     *
     * Transfer function: H(ω) = 1 / (1 + λ/ω²)
     *
     * where ω is spatial frequency
     *
     * λ acts as regularization (Tikhonov):
     *   - High frequencies (ω²>>λ): H≈1 (pass through)
     *   - Low frequencies (ω²<<λ): H≈ω²/λ (attenuated)
     *
     * Effect:
     *   - Large λ: Preserves all frequencies (stays close to φ₀)
     *   - Small λ: Allows low-frequency changes (smooth reconstruction)
     *
     * ====================================================================
     * CURRENT VALUE: λ = 0.5
     * ====================================================================
     *
     * Balanced approach for typical MRI velocity unwrapping
     */
    const lambda = 0.5;

    // // Count active pixels (for statistics/diagnostics)
    // let activeCount = 0;
    // for (let i = 0; i < n; i++) {
    //   if (mask[i]) activeCount++;
    // }

    let iter = 0;
    let maxChange = Infinity;

    // -------------------------------------------------------------------------
    // ITERATIVE SOLVER LOOP
    // -------------------------------------------------------------------------
    while (iter < this._maxIterations && maxChange > this._tolerance) {
      maxChange = 0;

      /**
       * RED-BLACK GAUSS-SEIDEL ORDERING
       *
       * ================================================================
       * CONCEPT
       * ================================================================
       *
       * Standard lexicographic ordering:
       *   Update pixels row-by-row: (0,0), (0,1), ..., (H-1,W-1)
       *
       * Red-black ordering:
       *   Checkerboard pattern, update in two sweeps:
       *
       *   Red pixels (x+y even):    Black pixels (x+y odd):
       *   R - R - R -                - B - B - B
       *   - R - R - R                B - B - B -
       *   R - R - R -                - B - B - B
       *
       * ================================================================
       * ADVANTAGES
       * ================================================================
       *
       * 1. Data independence:
       *    - All red pixels can update simultaneously (no dependencies)
       *    - All black pixels can update simultaneously
       *    - Enables parallelization (not used here, but possible)
       *
       * 2. Cache efficiency:
       *    - Better memory access pattern
       *    - Neighbors loaded together
       *
       * 3. Convergence:
       *    - Slightly faster than lexicographic for 2D Laplace
       *    - More symmetric update pattern
       *
       * ================================================================
       * IMPLEMENTATION
       * ================================================================
       *
       * Color 0 (red):   (x+y) % 2 == 0
       * Color 1 (black): (x+y) % 2 == 1
       *
       * Example 4×4 grid:
       *   0 1 0 1    ← (x+y) % 2
       *   1 0 1 0
       *   0 1 0 1
       *   1 0 1 0
       */

      for (let color = 0; color < 2; color++) {
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            if ((x + y) % 2 !== color) continue; // Skip wrong color

            const i = y * w + x;
            if (!mask[i]) continue; // Skip masked-out pixels

            const oldVal = phi[i];

            // -----------------------------------------------------------------
            // COMPUTE LAPLACIAN STENCIL: sum of neighbors
            // -----------------------------------------------------------------
            /**
             * 5-point stencil for Laplacian:
             *
             *        [up]
             *   [left] φ [right]
             *       [down]
             *
             * ∇²φ ≈ (φ_left + φ_right + φ_up + φ_down - 4φ) / h²
             *
             * For Neumann BC at ROI boundary:
             *   - Only include neighbors inside mask
             *   - Natural BC: ∂φ/∂n = 0 at boundary
             *   - count = actual number of valid neighbors (≤ 4)
             */

            let sum = 0; // Sum of neighbor values
            let count = 0; // Number of valid neighbors

            // Check and accumulate each neighbor
            if (x > 0 && mask[i - 1]) {
              sum += phi[i - 1];
              count++;
            } // Left
            if (x < w - 1 && mask[i + 1]) {
              sum += phi[i + 1];
              count++;
            } // Right
            if (y > 0 && mask[i - w]) {
              sum += phi[i - w];
              count++;
            } // Up
            if (y < h - 1 && mask[i + w]) {
              sum += phi[i + w];
              count++;
            } // Down

            if (count > 0) {
              // ---------------------------------------------------------------
              // GAUSS-SEIDEL UPDATE WITH FIDELITY TERM
              // ---------------------------------------------------------------
              /**
               * Derivation:
               *
               * Equation: ∇²φ - λ(φ - φ₀) = rhs
               *
               * Discrete form:
               *   (sum - count·φ) - λ(φ - φ₀) = rhs
               *
               * Rearrange:
               *   sum - count·φ - λ·φ + λ·φ₀ = rhs
               *   -φ(count + λ) = rhs - sum - λ·φ₀
               *   φ(count + λ) = sum - rhs + λ·φ₀
               *
               * Solve for φ:
               *   φ_GS = (sum - rhs + λ·φ₀) / (count + λ)
               *
               * Special cases:
               *   - λ=0: φ_GS = (sum - rhs) / count (pure Poisson)
               *   - λ→∞: φ_GS → φ₀ (pure fidelity)
               *   - λ=0.5, count=4: denominator = 4.5 (balanced)
               */

              const newVal
                = (sum - rhs[i] + lambda * originalData[i]) / (count + lambda);

              // ---------------------------------------------------------------
              // SOR (SUCCESSIVE OVER-RELAXATION)
              // ---------------------------------------------------------------
              /**
               * Standard Gauss-Seidel:
               *   φ^(k+1) = φ_GS
               *
               * SOR:
               *   φ^(k+1) = φ^k + ω(φ_GS - φ^k)
               *
               * Expanded:
               *   φ^(k+1) = φ^k + ω·φ_GS - ω·φ^k
               *           = (1-ω)φ^k + ω·φ_GS
               *
               * Interpretation:
               *   - ω=1: full Gauss-Seidel step
               *   - ω>1: over-shoot (accelerate)
               *   - ω<1: under-shoot (dampen)
               *
               * Current: ω=1.8 (over-relaxation)
               *   → Take 1.8× the Gauss-Seidel step
               *   → Faster convergence (if stable)
               */

              phi[i] = oldVal + this._omega * (newVal - oldVal);

              // Track maximum change for convergence check
              const change = Math.abs(phi[i] - oldVal);
              maxChange = Math.max(maxChange, change);
            }
          }
        }
      }

      iter++;

      // Log progress periodically
      if (
        this._logDiagnostics
        && (iter % 100 === 0 || iter === this._maxIterations)
      ) {
        console.log(
          `  Iteration ${iter}: maxChange=${maxChange.toExponential(3)}`,
        );
      }
    }

    // -------------------------------------------------------------------------
    // CONVERGENCE CHECK
    // -------------------------------------------------------------------------
    if (iter >= this._maxIterations) {
      console.warn(
        `Poisson solver did not converge (maxChange=${maxChange.toExponential(
          3,
        )} > tol=${this._tolerance})`,
      );
    }
    else {
      console.log(
        `Converged in ${iter} iterations (maxChange=${maxChange.toExponential(
          3,
        )})`,
      );
    }

    /**
     * NO DC OFFSET REMOVAL
     *
     * Pure Poisson (λ=0):
     *   - Solution unique only up to constant
     *   - Need to remove arbitrary DC offset: φ ← φ - mean(φ)
     *
     * With fidelity term (λ>0):
     *   - Solution anchored to original data φ₀
     *   - DC level determined by φ₀ (no arbitrary constant)
     *   - No offset removal needed
     */

    return phi;
  }

  // ============================================================================
  // TEMPORAL UNWRAPPING: Cyclic mode (simple 3-pass approach)
  // ============================================================================
  /**
   * Cyclic temporal unwrapping with 3 passes
   *
   * ========================================================================
   * MATHEMATICAL FRAMEWORK
   * ========================================================================
   *
   * For a periodic cardiac cycle with N frames:
   *   φ[0], φ[1], ..., φ[N-1], (φ[0])
   *
   * Cyclic boundary condition:
   *   φ[0] ≈ φ[N-1] (end connects to beginning)
   *
   * Objective: Find unwrap offsets k[t] ∈ ℤ such that:
   *   φ_unwrapped[t] = φ_wrapped[t] + k[t]·2venc
   *
   * Subject to:
   *   1. Smooth transitions: |φ[t+1] - φ[t]| ≤ threshold
   *   2. Cyclic closure: |φ[0] - φ[N-1]| ≤ threshold
   *
   * ========================================================================
   * ALGORITHM
   * ========================================================================
   *
   * Pass 1 - Forward (t=0→N-1):
   *   For each pixel i, for each frame t=1,...,N-1:
   *     diff = φ[t,i] - φ[t-1,i]
   *     If |diff| > threshold: φ[t,i] ← φ[t,i] ∓ 2venc
   *
   * Pass 2 - Backward (t=N-1→0):
   *   For each pixel i, for each frame t=N-2,...,0:
   *     diff = φ[t,i] - φ[t+1,i]
   *     If |diff| > threshold: φ[t,i] ← φ[t,i] ∓ 2venc
   *
   * Pass 3 - Cyclic closure (boundary):
   *   For each pixel i:
   *     diff = φ[0,i] - φ[N-1,i]
   *     If |diff| > threshold: φ[0,i] ← φ[0,i] ∓ 2venc
   *
   * ========================================================================
   * RATIONALE
   * ========================================================================
   *
   * Why 3 passes?
   *
   * 1. Forward pass:
   *    - Unwraps propagating forward in time
   *    - May accumulate errors (early mistake affects all later frames)
   *
   * 2. Backward pass:
   *    - Corrects errors from forward pass
   *    - Unwraps propagating backward in time
   *    - Bidirectional consensus improves robustness
   *
   * 3. Cyclic closure pass:
   *    - Enforces periodicity: φ[N-1] → φ[0]
   *    - Corrects accumulated offset around cycle
   *    - Ensures smooth loop closure
   *
   * ========================================================================
   * COMPARISON WITH LINEAR MODE
   * ========================================================================
   *
   * Linear mode (passes 1&2 only):
   *   ✓ Safe for any data (periodic or not)
   *   ✗ May leave discontinuity: φ[N-1] ↛ φ[0]
   *
   * Cyclic mode (passes 1&2&3):
   *   ✓ Enforces periodicity
   *   ✓ Better for true cardiac cycles
   *   ✗ May over-correct non-periodic data
   *
   * ========================================================================
   * TRADE-OFFS
   * ========================================================================
   *
   * vs Global reference frame method (more complex):
   *   ✓ Simpler implementation
   *   ✓ Builds on existing linear approach
   *   ✓ Easy to understand and debug
   *   ✗ Less robust to noise (sequential processing)
   *   ✗ May accumulate errors around cycle
   *   ✗ No optimal reference frame selection
   *
   * When to use cyclic mode:
   *   ✓ True cardiac cine (end-diastole to end-diastole)
   *   ✓ Clean data with moderate aliasing
   *   ✓ Observed discontinuity at boundary with linear mode
   *
   * When to use linear mode:
   *   ✓ Uncertain about periodicity
   *   ✓ Very noisy data
   *   ✓ Non-periodic sequences
   *   ✓ Conservative/safe approach
   *
   * ========================================================================
   * PARAMETERS
   * ========================================================================
   *
   * @param frames - Velocity frames [φ[0], ..., φ[N-1]] (modified in-place)
   * @param mask - Processing mask (1=active, 0=skip)
   *
   * @returns {fwd, bwd, cyclic}
   *   - fwd: Number of corrections in forward pass
   *   - bwd: Number of corrections in backward pass
   *   - cyclic: Number of corrections in cyclic closure pass
   */
  private _unwrapTemporalCyclic(frames: Float32Array[], mask: Uint8Array) {
    const n = this.width * this.height; // Pixels per frame
    const numFrames = frames.length; // Number of cardiac phases
    const wrapRange = 2 * this.venc; // Wrap correction amount

    /**
     * Temporal unwrap threshold
     *
     * Formula: threshold = venc × (1 + buffer)
     *
     * Current: 1.05 → 5% buffer
     *
     * ====================================================================
     * RATIONALE FOR SMALLER BUFFER (vs spatial 20%)
     * ====================================================================
     *
     * Temporal vs Spatial characteristics:
     *
     * Temporal (between cardiac phases):
     *   - Physiologically smooth (continuous cardiac motion)
     *   - Large jumps likely indicate aliasing
     *   - Less affected by anatomical edges
     *   - Can use smaller buffer (more aggressive)
     *
     * Spatial (within frame):
     *   - Anatomical boundaries (vessel walls, chambers)
     *   - Turbulent flow regions
     *   - Partial volume effects
     *   - Needs larger buffer (more conservative)
     *
     * ====================================================================
     * PARAMETER TUNING
     * ====================================================================
     *
     * Buffer = 0.03-0.05 (3-5%): Very tight
     *   ✓ Catches subtle temporal wraps
     *   ✗ May unwrap noise/arrhythmia
     *   Use: Clean data, regular cardiac rhythm
     *
     * Buffer = 0.05-0.10 (5-10%): Balanced (RECOMMENDED)
     *   ✓ Good for typical cardiac cine
     *   ✓ Handles moderate noise
     *   Use: Standard application
     *
     * Buffer = 0.10-0.20 (10-20%): Conservative
     *   ✓ Robust to noise
     *   ✓ Handles irregular rhythms
     *   ✗ May miss subtle wraps
     *   Use: Noisy data, arrhythmia, variable R-R intervals
     *
     * ====================================================================
     * EXAMPLE (venc = 150 cm/s)
     * ====================================================================
     *
     * Buffer 0.05 → threshold = 157.5 cm/s
     *
     * Temporal velocity change interpretation:
     *   < 157.5 cm/s: Smooth cardiac motion (keep)
     *   > 157.5 cm/s: Likely aliasing (unwrap)
     *
     * Typical cardiac flow:
     *   - Systole: rapid increase (50-100 cm/s/phase)
     *   - Diastole: slow decrease (10-30 cm/s/phase)
     *   - Peak velocity: ~100-150 cm/s
     *
     * Jump > 157.5 suggests wrap, not physiological change
     */
    const temporalThreshold = this.venc * 1.05;

    let fwd = 0;
    let bwd = 0;
    let cyclic = 0; // Correction counters

    // -------------------------------------------------------------------------
    // PASS 1: Forward unwrapping (t=0→N-1)
    // -------------------------------------------------------------------------
    /**
     * Forward temporal propagation
     *
     * For each pixel independently:
     *   Start at t=0 (assumed unwrapped)
     *   For t=1,...,N-1:
     *     Compare φ[t] with φ[t-1]
     *     If discontinuity detected: correct φ[t]
     *
     * Assumption: φ[t] should be close to φ[t-1]
     *   (Smooth cardiac motion between adjacent phases)
     *
     * Unwrap decision:
     *   diff = φ[t] - φ[t-1]
     *
     *   If diff > +threshold:
     *     → φ[t] jumped too high (wrapped down from +venc to -venc)
     *     → Correct: φ[t] ← φ[t] - 2venc
     *
     *   If diff < -threshold:
     *     → φ[t] jumped too low (wrapped up from -venc to +venc)
     *     → Correct: φ[t] ← φ[t] + 2venc
     */

    for (let i = 0; i < n; i++) {
      if (!mask[i]) continue; // Skip pixels outside ROI

      for (let t = 1; t < numFrames; t++) {
        const diff = frames[t][i] - frames[t - 1][i];

        if (diff > temporalThreshold) {
          frames[t][i] -= wrapRange; // Wrapped high: correct downward
          fwd++;
        }
        else if (diff < -temporalThreshold) {
          frames[t][i] += wrapRange; // Wrapped low: correct upward
          fwd++;
        }
        // else: |diff| ≤ threshold → smooth transition, no correction
      }
    }

    // -------------------------------------------------------------------------
    // PASS 2: Backward unwrapping (t=N-1→0)
    // -------------------------------------------------------------------------
    /**
     * Backward temporal propagation
     *
     * For each pixel independently:
     *   Start at t=N-1 (now corrected by forward pass)
     *   For t=N-2,...,0:
     *     Compare φ[t] with φ[t+1]
     *     If discontinuity detected: correct φ[t]
     *
     * Purpose:
     *   - Catches wraps missed by forward pass
     *   - Corrects errors introduced by forward pass
     *   - Refines unwrapping with additional information
     *
     * Why backward after forward?
     *   - Forward: propagates from t=0 (may accumulate errors)
     *   - Backward: propagates from t=N-1 (corrects accumulated errors)
     *   - Bidirectional consensus → more robust result
     *
     * Example benefit:
     *   Forward pass: mistake at t=5 affects frames 5,6,7,...
     *   Backward pass: correct information from t>7 fixes t=5
     */

    for (let i = 0; i < n; i++) {
      if (!mask[i]) continue;

      for (let t = numFrames - 2; t >= 0; t--) {
        const diff = frames[t][i] - frames[t + 1][i];

        if (diff > temporalThreshold) {
          frames[t][i] -= wrapRange;
          bwd++;
        }
        else if (diff < -temporalThreshold) {
          frames[t][i] += wrapRange;
          bwd++;
        }
      }
    }

    // -------------------------------------------------------------------------
    // PASS 3: Cyclic closure correction (N-1→0 boundary)
    // -------------------------------------------------------------------------
    /**
     * Enforce cyclic boundary condition
     *
     * For a true periodic cardiac cycle:
     *   - Frame 0: end-diastole (cardiac phase ≈ 0°)
     *   - Frame N-1: end-diastole (cardiac phase ≈ 360°)
     *   - Should have: φ[0] ≈ φ[N-1]
     *
     * After forward/backward passes:
     *   - Temporal smoothness enforced: φ[t] ≈ φ[t±1]
     *   - But cycle may not close: φ[N-1] ↛ φ[0]
     *   - Accumulated offset around loop
     *
     * Closure correction:
     *   Check boundary: diff = φ[0] - φ[N-1]
     *   If |diff| > threshold: adjust φ[0] to close loop
     *
     * Why correct φ[0] and not φ[N-1]?
     *   - Arbitrary choice (either could be corrected)
     *   - Correcting φ[0]: maintains consistency with φ[1]
     *   - Alternative: distribute correction across all frames
     *
     * Mathematical interpretation:
     *   - Find k ∈ ℤ such that: |φ[0] + k·2venc - φ[N-1]| is minimized
     *   - k = ±1 for single wrap correction
     *   - k = 0 if already closed
     */

    for (let i = 0; i < n; i++) {
      if (!mask[i]) continue;

      // Check discontinuity across cycle boundary
      const diff = frames[0][i] - frames[numFrames - 1][i];

      if (Math.abs(diff) > temporalThreshold) {
        // Discontinuity detected at cycle boundary

        if (diff > temporalThreshold) {
          // φ[0] too high relative to φ[N-1]

          frames[0][i] -= wrapRange; // Correct downward
          cyclic++;
        }
        else if (diff < -temporalThreshold) {
          // φ[0] too low relative to φ[N-1]

          frames[0][i] += wrapRange; // Correct upward
          cyclic++;
        }
      }
      // else: cycle already closed (|diff| ≤ threshold)
    }

    // -------------------------------------------------------------------------
    // DIAGNOSTICS: Report cyclic closure quality
    // -------------------------------------------------------------------------
    /**
     * Post-correction validation
     *
     * Compute average discontinuity at cycle boundary:
     *   Δ_cyclic = (1/N) Σ_i |φ[0,i] - φ[N-1,i]|
     *
     * Quality assessment:
     *   Δ_cyclic << threshold: Excellent closure
     *   Δ_cyclic ≈ threshold: Acceptable closure
     *   Δ_cyclic > threshold: Poor closure (warning)
     *
     * Poor closure may indicate:
     *   - Data not truly periodic (incomplete cardiac cycle)
     *   - Severe aliasing (multiple wraps)
     *   - Arrhythmia or irregular R-R intervals
     *   - Noisy data
     *   - Need for more sophisticated method (e.g., global reference)
     */

    if (this._logDiagnostics) {
      let totalDisc = 0;
      let countPixels = 0;

      // Compute average boundary discontinuity after correction
      for (let i = 0; i < n; i++) {
        if (!mask[i]) continue;
        totalDisc += Math.abs(frames[0][i] - frames[numFrames - 1][i]);
        countPixels++;
      }

      if (countPixels > 0) {
        const avgDisc = totalDisc / countPixels;
        console.log(
          `  Cyclic closure: avg|φ[0]-φ[N-1]| = ${avgDisc.toFixed(4)} `
          + `(threshold=${temporalThreshold.toFixed(4)})`,
        );

        if (avgDisc > temporalThreshold) {
          console.warn(
            `  ⚠ Cycle closure incomplete (${avgDisc.toFixed(
              4,
            )} > ${temporalThreshold.toFixed(4)}). `
            + `Data may not be truly periodic. Consider: `
            + `1) Using linear mode, 2) Checking cardiac gating, 3) Verifying full cycle captured`,
          );
        }
      }
    }

    return { fwd, bwd, cyclic };
  }

  // ============================================================================
  // TEMPORAL UNWRAPPING: Linear mode (standard bidirectional)
  // ============================================================================
  /**
   * Linear temporal unwrapping (forward + backward passes)
   *
   * Standard bidirectional approach without cyclic constraint.
   * Safe for all data types (periodic or non-periodic).
   *
   * Algorithm: Same as cyclic mode passes 1 & 2, but no closure pass
   *
   * Use when:
   *   - Uncertain about data periodicity
   *   - Non-periodic temporal sequence
   *   - Conservative/safe approach desired
   *
   * @param frames - Velocity frames (modified in-place)
   * @param mask - Processing mask
   * @returns {fwd, bwd} - Correction counts for each direction
   */

  private _unwrapTemporalLinear(frames: Float32Array[], mask: Uint8Array) {
    const n = this.width * this.height;
    const wrapRange = 2 * this.venc;
    const temporalThreshold = this.venc * 1.05;
    let fwd = 0;
    let bwd = 0;

    // Forward pass: t=0→N-1
    for (let i = 0; i < n; i++) {
      if (!mask[i]) continue;
      for (let t = 1; t < frames.length; t++) {
        const diff = frames[t][i] - frames[t - 1][i];

        if (diff > temporalThreshold) {
          frames[t][i] -= wrapRange;
          fwd++;
        }
        else if (diff < -temporalThreshold) {
          frames[t][i] += wrapRange;
          fwd++;
        }
      }
    }

    // Backward pass: t=N-1→0
    for (let i = 0; i < n; i++) {
      if (!mask[i]) continue;
      for (let t = frames.length - 2; t >= 0; t--) {
        const diff = frames[t][i] - frames[t + 1][i];

        if (diff > temporalThreshold) {
          frames[t][i] -= wrapRange;
          bwd++;
        }
        else if (diff < -temporalThreshold) {
          frames[t][i] += wrapRange;
          bwd++;
        }
      }
    }

    return { fwd, bwd };
  }

  // ============================================================================
  // DIAGNOSTICS
  // ============================================================================
  /**
   * Computes maximum absolute difference between 4-connected neighbors
   *
   * Used for early exit check: if maxJump ≤ threshold, no unwrapping needed
   *
   * 4-neighborhood: right (+x) and down (+y) neighbors only
   *   (Sufficient since checking all pairs would double-count)
   *
   * @param v - Velocity field
   * @param mask - Processing mask
   * @returns Maximum absolute neighbor difference in masked region
   */
  private _maxAbsNeighborJump(v: Float32Array, mask: Uint8Array): number {
    const w = this.width;
    const h = this.height;
    let m = 0;

    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        const i = row + x;
        if (!mask[i]) continue;

        // Check right neighbor (x+1)
        if (x + 1 < w) {
          const j = i + 1;
          if (mask[j]) m = Math.max(m, Math.abs(v[j] - v[i]));
        }

        // Check down neighbor (y+1)
        if (y + 1 < h) {
          const j = i + w;
          if (mask[j]) m = Math.max(m, Math.abs(v[j] - v[i]));
        }
      }
    }
    return m;
  }

  // ============================================================================
  // ROI UTILITIES
  // ============================================================================

  private static _validateROI(roi: ROI) {
    if (
      !roi
      || !roi.bbox
      || !Array.isArray(roi.polygon)
      || roi.polygon.length < 3
    ) {
      throw new Error('roi must have bbox and polygon (>=3 points).');
    }
    const { xMin, yMin, xMax, yMax } = roi.bbox;
    [xMin, yMin, xMax, yMax].forEach((v) => {
      if (!Number.isFinite(v))
        throw new Error('roi.bbox values must be finite numbers.');
    });
    roi.polygon.forEach((p) => {
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) {
        throw new TypeError('roi.polygon points must be finite numbers.');
      }
    });
  }

  /**
   * Builds binary mask from ROI polygon
   *
   * Algorithm: Ray-casting for point-in-polygon test
   * Sampling: Pixel centers (x+0.5, y+0.5)
   */
  private _buildROIMask(n: number, roi: ROI): Uint8Array {
    const mask = new Uint8Array(n);
    const { width, height } = this;

    // Compute bounding box (optimization: skip pixels far from ROI)
    const x0 = Math.max(0, Math.floor(Math.min(roi.bbox.xMin, roi.bbox.xMax)));
    const y0 = Math.max(0, Math.floor(Math.min(roi.bbox.yMin, roi.bbox.yMax)));
    const x1 = Math.min(
      width - 1,
      Math.ceil(Math.max(roi.bbox.xMin, roi.bbox.xMax)),
    );
    const y1 = Math.min(
      height - 1,
      Math.ceil(Math.max(roi.bbox.yMin, roi.bbox.yMax)),
    );

    for (let y = y0; y <= y1; y++) {
      const row = y * width;
      const py = y + 0.5; // Sample at pixel center
      for (let x = x0; x <= x1; x++) {
        const px = x + 0.5;
        if (VelocityAntiAliaser._pointInPolygon(px, py, roi.polygon))
          mask[row + x] = 1;
      }
    }
    return mask;
  }

  /**
   * Ray-casting algorithm for point-in-polygon test
   *
   * Casts horizontal ray from point to infinity
   * Counts intersections with polygon edges
   * Odd intersections = inside, even = outside
   */
  private static _pointInPolygon(
    x: number,
    y: number,
    poly: Array<{ x: number; y: number }>,
  ): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      const intersect
        = (yi > y) !== (yj > y)
          && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-30) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  private static _fullMask(n: number): Uint8Array {
    const m = new Uint8Array(n);
    m.fill(1);
    return m;
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Creates Float32Array copy of input
   * Ensures proper ArrayBuffer backing for TypeScript compatibility
   */
  private static _toFloat32Copy(input: Float32Array | number[]): Float32Array {
    if (input instanceof Float32Array) {
      const out = new Float32Array(input.length);
      out.set(input);
      return out;
    }
    return new Float32Array(input);
  }

  /**
   * High-resolution timer (ms)
   * Uses performance.now() if available, falls back to Date.now()
   */
  private static _nowMs(): number {
    const p = globalThis.performance;
    return p?.now() ?? Date.now();
  }
}
